import { useState, useMemo } from "react";
import { useAccount } from "wagmi";
import {
  Layers,
  Image,
  Lock,
  Eye,
  EyeOff,
  Users,
  Shield,
  ExternalLink,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  Info,
  Palette,
  Settings,
  FileJson,
  Wallet,
} from "lucide-react";
import { isValidAddress, cn, explorerUrl } from "../lib/utils";
import { ADDRESSES } from "../config/contracts";

// ─── Types ───────────────────────────────────────────────────────────────────

interface CollectionFeatures {
  publicMint: boolean;
  reveal: boolean;
  whitelist: boolean;
  soulbound: boolean;
}

interface CollectionConfig {
  name: string;
  symbol: string;
  description: string;
  totalSupply: string;
  mintPrice: string;
  royaltyBps: string;
  royaltyAddress: string;
  baseURI: string;
  features: CollectionFeatures;
  creator: string;
  chain: number;
  chainName: string;
  treasury: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function SectionHeader({
  icon: Icon,
  title,
  subtitle,
}: {
  icon: React.ElementType;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="flex items-start gap-2.5 mb-4">
      <div className="w-8 h-8 rounded-button bg-secondary/10 border border-secondary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon className="w-4 h-4 text-secondary" />
      </div>
      <div>
        <h3 className="font-display font-semibold text-text-primary text-sm">{title}</h3>
        {subtitle && <p className="text-xs text-text-tertiary mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
}

interface FeatureToggleProps {
  icon: React.ElementType;
  title: string;
  description: string;
  active: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  disabledReason?: string;
}

function FeatureToggle({
  icon: Icon,
  title,
  description,
  active,
  onChange,
  disabled,
  disabledReason,
}: FeatureToggleProps) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!active)}
      className={cn(
        "w-full text-left p-3 rounded-card border transition-all duration-200",
        disabled
          ? "opacity-50 cursor-not-allowed border-border bg-surface/30"
          : active
          ? "border-secondary/40 bg-secondary/8 cursor-pointer hover:border-secondary/60"
          : "border-border bg-surface/50 cursor-pointer hover:border-border-bright"
      )}
      title={disabled ? disabledReason : undefined}
    >
      <div className="flex items-start gap-2.5">
        <div
          className={cn(
            "w-7 h-7 rounded-button flex items-center justify-center flex-shrink-0 mt-0.5",
            active && !disabled ? "bg-secondary/15 text-secondary" : "bg-surface text-text-tertiary"
          )}
        >
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn("text-xs font-semibold", active && !disabled ? "text-text-primary" : "text-text-secondary")}>
              {title}
            </span>
            {/* Toggle pill */}
            <div
              className={cn(
                "relative w-7 h-4 rounded-full border transition-all duration-200 flex items-center",
                active && !disabled
                  ? "bg-secondary/70 border-secondary/60"
                  : "bg-surface-hover border-border"
              )}
            >
              <div
                className={cn(
                  "absolute w-2.5 h-2.5 rounded-full bg-white transition-all duration-200",
                  active && !disabled ? "translate-x-3.5" : "translate-x-0.5"
                )}
              />
            </div>
          </div>
          <p className="text-[10px] text-text-tertiary mt-0.5 leading-relaxed">{description}</p>
        </div>
      </div>
    </button>
  );
}

// ─── JSON Preview panel ───────────────────────────────────────────────────────

interface JSONPreviewProps {
  config: CollectionConfig;
}

function JSONPreview({ config }: JSONPreviewProps) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const royaltyBpsNum = parseInt(config.royaltyBps) || 0;

  const jsonObj = {
    name: config.name || "Unnamed Collection",
    symbol: config.symbol || "NFT",
    description: config.description || "",
    totalSupply: parseInt(config.totalSupply) || 0,
    mintPrice: config.mintPrice || "0",
    mintPriceCurrency: "JASMY",
    royaltyBps: royaltyBpsNum,
    royaltyPercent: (royaltyBpsNum / 100).toFixed(2) + "%",
    royaltyAddress: config.royaltyAddress || ADDRESSES.TREASURY,
    treasury: ADDRESSES.TREASURY,
    baseURI: config.baseURI || "https://api.example.com/metadata/{id}.json",
    features: {
      publicMint: config.features.publicMint,
      reveal: config.features.reveal,
      whitelist: config.features.whitelist,
      soulbound: config.features.soulbound,
    },
    chain: {
      id: 680,
      name: "JasmyChain",
      currency: "JASMY",
    },
    standard: "ERC-721",
    generatedAt: new Date().toISOString(),
  };

  const jsonStr = JSON.stringify(jsonObj, null, 2);

  function copyJSON() {
    navigator.clipboard.writeText(jsonStr).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="glass-card overflow-hidden">
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-surface/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <FileJson className="w-4 h-4 text-secondary" />
          <span className="text-sm font-semibold text-text-primary font-display">Collection Config JSON</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); copyJSON(); }}
            className="p-1.5 rounded-button border border-border hover:border-secondary/40 text-text-tertiary hover:text-secondary transition-all"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-secondary" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
          {expanded ? <ChevronUp className="w-4 h-4 text-text-tertiary" /> : <ChevronDown className="w-4 h-4 text-text-tertiary" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border">
          <pre className="p-4 text-[11px] text-text-secondary font-mono leading-relaxed overflow-x-auto max-h-80 scrollbar-thin">
            {jsonStr}
          </pre>
        </div>
      )}
    </div>
  );
}

// ─── Visual preview card ──────────────────────────────────────────────────────

interface CollectionPreviewCardProps {
  config: CollectionConfig;
}

function CollectionPreviewCard({ config }: CollectionPreviewCardProps) {
  const displayName = config.name.trim() || "Collection Name";
  const displaySymbol = config.symbol.trim() || "NFT";
  const initials = displaySymbol.slice(0, 3).toUpperCase();
  const totalSupply = parseInt(config.totalSupply) || 0;
  const mintPrice = parseFloat(config.mintPrice) || 0;
  const royaltyBps = parseInt(config.royaltyBps) || 0;

  const activeFeatures = Object.entries(config.features)
    .filter(([, v]) => v)
    .map(([k]) => k);

  const featureLabels: Record<string, string> = {
    publicMint: "Public Mint",
    reveal: "Reveal",
    whitelist: "Whitelist",
    soulbound: "Soulbound",
  };

  return (
    <div className="glass-card p-5 glow-purple">
      {/* Collection image placeholder */}
      <div className="w-full aspect-square rounded-card bg-surface border border-border flex items-center justify-center mb-4 overflow-hidden relative">
        <div
          className="absolute inset-0"
          style={{
            background: "radial-gradient(ellipse at center, rgba(139,47,201,0.15) 0%, rgba(7,8,22,0) 70%)",
          }}
        />
        <div className="relative text-center">
          <div className="text-5xl font-bold font-display text-secondary/60 mb-1">{initials}</div>
          <div className="text-xs text-text-tertiary">Collection Preview</div>
        </div>
      </div>

      {/* Name and symbol */}
      <div className="mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-display font-bold text-text-primary">{displayName}</h3>
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-secondary/10 text-secondary border-secondary/20">
            ERC-721
          </span>
        </div>
        <p className="text-xs text-text-tertiary">{displaySymbol}</p>
      </div>

      {config.description && (
        <p className="text-xs text-text-tertiary line-clamp-2 mb-3">{config.description}</p>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-2 text-xs mb-3">
        <div className="bg-surface/50 border border-border rounded-button p-2">
          <div className="text-text-tertiary mb-0.5">Supply</div>
          <div className="text-text-primary font-semibold">
            {totalSupply > 0 ? totalSupply.toLocaleString() : "—"}
          </div>
        </div>
        <div className="bg-surface/50 border border-border rounded-button p-2">
          <div className="text-text-tertiary mb-0.5">Mint Price</div>
          <div className="text-jasmy font-semibold">
            {mintPrice > 0 ? mintPrice + " JASMY" : "—"}
          </div>
        </div>
        <div className="bg-surface/50 border border-border rounded-button p-2">
          <div className="text-text-tertiary mb-0.5">Royalty</div>
          <div className="text-text-primary font-semibold">
            {royaltyBps > 0 ? (royaltyBps / 100).toFixed(1) + "%" : "0%"}
          </div>
        </div>
        <div className="bg-surface/50 border border-border rounded-button p-2">
          <div className="text-text-tertiary mb-0.5">Chain</div>
          <div className="text-jasmy font-semibold">JasmyChain</div>
        </div>
      </div>

      {/* Active features */}
      {activeFeatures.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {activeFeatures.map((f) => (
            <span
              key={f}
              className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-secondary/10 text-secondary border border-secondary/20"
            >
              {featureLabels[f]}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Deploy info banner ──────────────────────────────────────────────────────

function DeployBanner({ config, isConnected }: { config: CollectionConfig; isConnected: boolean }) {
  const isReady =
    config.name.trim().length >= 1 &&
    config.symbol.trim().length >= 1 &&
    parseInt(config.totalSupply) > 0;

  return (
    <div className="glass-card p-5 border border-secondary/20">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-8 h-8 rounded-button bg-secondary/10 flex items-center justify-center flex-shrink-0">
          <Wallet className="w-4 h-4 text-secondary" />
        </div>
        <div>
          <h4 className="font-display font-semibold text-text-primary text-sm">Deploy Your Collection</h4>
          <p className="text-xs text-text-tertiary mt-0.5">
            NFT factory contract coming to JasmyChain — export your config now and deploy when ready.
          </p>
        </div>
      </div>

      <div className="bg-surface/50 border border-border rounded-card p-3 mb-4">
        <div className="flex items-start gap-2">
          <Info className="w-3.5 h-3.5 text-text-tertiary flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-text-tertiary leading-relaxed">
            The NFT factory contract for JasmyChain is in development. Export your collection config as JSON
            and deploy via the JasmyChain explorer when the factory is live. Your config is fully
            spec-compliant with ERC-721 and EIP-2981 royalty standards.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <a
          href="https://explorer.jasmyscan.net"
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            "btn-secondary w-full inline-flex items-center justify-center gap-2 text-sm",
            !isReady && "opacity-40 pointer-events-none"
          )}
        >
          Open JasmyScan Explorer <ExternalLink className="w-4 h-4" />
        </a>
        <div className="text-center text-xs text-text-tertiary">
          {isConnected ? (
            isReady ? (
              "Your config is ready to export — click Copy JSON in the preview panel."
            ) : (
              "Fill in Name, Symbol, and Supply to enable export."
            )
          ) : (
            "Connect your wallet to deploy when the factory is available."
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export function CollectionStationPage() {
  const { isConnected, address } = useAccount();

  const [name, setName] = useState("");
  const [symbol, setSymbol] = useState("");
  const [description, setDescription] = useState("");
  const [totalSupply, setTotalSupply] = useState("");
  const [mintPrice, setMintPrice] = useState("");
  const [royaltyBps, setRoyaltyBps] = useState("500");
  const [royaltyAddress, setRoyaltyAddress] = useState("");
  const [baseURI, setBaseURI] = useState("");
  const [features, setFeatures] = useState<CollectionFeatures>({
    publicMint: true,
    reveal: false,
    whitelist: false,
    soulbound: false,
  });

  // Auto-populate royalty address from connected wallet
  const effectiveRoyaltyAddress = royaltyAddress || address || ADDRESSES.TREASURY;

  const config: CollectionConfig = useMemo(
    () => ({
      name,
      symbol,
      description,
      totalSupply,
      mintPrice,
      royaltyBps,
      royaltyAddress: effectiveRoyaltyAddress,
      baseURI,
      features,
      creator: address || "",
      chain: 680,
      chainName: "JasmyChain",
      treasury: ADDRESSES.TREASURY,
    }),
    [name, symbol, description, totalSupply, mintPrice, royaltyBps, effectiveRoyaltyAddress, baseURI, features, address]
  );

  function toggleFeature(key: keyof CollectionFeatures) {
    setFeatures((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  const royaltyPct = parseInt(royaltyBps) || 0;
  const royaltyAddressValid = !royaltyAddress || isValidAddress(royaltyAddress);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-20">
      {/* Page header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-card bg-secondary/10 border border-secondary/20 flex items-center justify-center">
            <Layers className="w-5 h-5 text-secondary" />
          </div>
          <div>
            <h1 className="font-display text-3xl font-bold" style={{ background: "linear-gradient(135deg, #8B2FC9 0%, #FF6EC7 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
              Collection Station
            </h1>
            <p className="text-text-secondary text-sm">
              Design and configure your ERC-721 NFT collection on JasmyChain
            </p>
          </div>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

        {/* ── Left: Form ──────────────────────────────────────────────────── */}
        <div className="lg:col-span-3 space-y-5">

          {/* Section 1: Identity */}
          <div className="glass-card p-5">
            <SectionHeader
              icon={Palette}
              title="Collection Identity"
              subtitle="Name, symbol, and description of your NFT collection"
            />
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-text-secondary mb-1.5">
                  Collection Name <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Mystic Jasmy Warriors"
                  className="input-field"
                  maxLength={64}
                />
                <div className="text-xs text-text-tertiary mt-1 text-right">{name.length}/64</div>
              </div>

              <div>
                <label className="block text-sm text-text-secondary mb-1.5">
                  Symbol <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  value={symbol}
                  onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                  placeholder="MJW"
                  className="input-field uppercase tracking-widest"
                  maxLength={10}
                />
                <div className="text-xs text-text-tertiary mt-1 text-right">{symbol.length}/10</div>
              </div>

              <div>
                <label className="block text-sm text-text-secondary mb-1.5">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe your collection — lore, art style, utility..."
                  className="input-field resize-none h-24"
                  maxLength={1000}
                />
                <div className="text-xs text-text-tertiary mt-1 text-right">{description.length}/1000</div>
              </div>
            </div>
          </div>

          {/* Section 2: Supply config */}
          <div className="glass-card p-5">
            <SectionHeader
              icon={Settings}
              title="Supply & Pricing"
              subtitle="Total supply, mint price, and metadata URI"
            />
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-text-secondary mb-1.5">
                  Total Supply <span className="text-error">*</span>
                </label>
                <input
                  type="number"
                  value={totalSupply}
                  onChange={(e) => setTotalSupply(e.target.value)}
                  placeholder="1000"
                  className="input-field"
                  min="1"
                  max="1000000"
                  step="1"
                />
                <p className="text-[11px] text-text-tertiary mt-1">Maximum number of NFTs that can ever be minted.</p>
              </div>

              <div>
                <label className="block text-sm text-text-secondary mb-1.5">Mint Price (JASMY)</label>
                <div className="relative">
                  <input
                    type="number"
                    value={mintPrice}
                    onChange={(e) => setMintPrice(e.target.value)}
                    placeholder="100"
                    className="input-field pr-16"
                    min="0"
                    step="any"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-text-tertiary text-sm">JASMY</span>
                </div>
                <p className="text-[11px] text-text-tertiary mt-1">Set to 0 for a free mint.</p>
              </div>

              <div>
                <label className="block text-sm text-text-secondary mb-1.5">Base URI</label>
                <input
                  type="url"
                  value={baseURI}
                  onChange={(e) => setBaseURI(e.target.value)}
                  placeholder="https://api.example.com/metadata/"
                  className="input-field"
                />
                <p className="text-[11px] text-text-tertiary mt-1">
                  Each token's metadata served at <span className="text-text-secondary font-mono">{"{baseURI}{tokenId}.json"}</span>
                </p>
              </div>
            </div>
          </div>

          {/* Section 3: Royalties */}
          <div className="glass-card p-5">
            <SectionHeader
              icon={Shield}
              title="Royalties (EIP-2981)"
              subtitle="On-chain royalty info — respected by compliant marketplaces"
            />
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-text-secondary mb-1.5">
                  Royalty Percentage
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="1500"
                    step="25"
                    value={royaltyBps}
                    onChange={(e) => setRoyaltyBps(e.target.value)}
                    className="flex-1 accent-secondary"
                  />
                  <div className="w-16 text-center">
                    <span className="text-lg font-bold font-display text-secondary">
                      {(royaltyPct / 100).toFixed(2)}%
                    </span>
                  </div>
                </div>
                <div className="flex justify-between text-[10px] text-text-tertiary mt-1">
                  <span>0% (none)</span>
                  <span>7.5% (recommended)</span>
                  <span>15% (max)</span>
                </div>
              </div>

              <div>
                <label className="block text-sm text-text-secondary mb-1.5">Royalty Address</label>
                <input
                  type="text"
                  value={royaltyAddress}
                  onChange={(e) => setRoyaltyAddress(e.target.value)}
                  placeholder={address || ADDRESSES.TREASURY}
                  className={cn(
                    "input-field font-mono text-sm",
                    royaltyAddress && !royaltyAddressValid && "border-error/50 focus:border-error"
                  )}
                />
                {royaltyAddress && !royaltyAddressValid && (
                  <p className="text-xs text-error mt-1">Invalid address format.</p>
                )}
                {!royaltyAddress && (
                  <p className="text-[11px] text-text-tertiary mt-1">
                    Defaults to {isConnected ? "your connected wallet" : ADDRESSES.TREASURY}.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Section 4: Features */}
          <div className="glass-card p-5">
            <SectionHeader
              icon={Layers}
              title="Collection Features"
              subtitle="Configure mint mechanics and access control"
            />
            <div className="space-y-2.5">
              <FeatureToggle
                icon={Users}
                title="Public Mint"
                description="Anyone can mint — no allowlist required. Disable for owner-only or whitelist-gated mints."
                active={features.publicMint}
                onChange={() => toggleFeature("publicMint")}
              />
              <FeatureToggle
                icon={features.reveal ? Eye : EyeOff}
                title="Reveal Mechanic"
                description="Hidden metadata (blind box) until a reveal event. Requires a reveal transaction to swap base URIs."
                active={features.reveal}
                onChange={() => toggleFeature("reveal")}
              />
              <FeatureToggle
                icon={Shield}
                title="Whitelist / Merkle Gating"
                description="Restrict minting to a Merkle-proof allowlist. Configure allowed addresses before launch."
                active={features.whitelist}
                onChange={() => toggleFeature("whitelist")}
                disabled={features.publicMint}
                disabledReason="Disable Public Mint to enable whitelist gating."
              />
              <FeatureToggle
                icon={Lock}
                title="Soulbound"
                description="Non-transferable NFTs — permanently bound to the minting wallet. Ideal for credentials or memberships."
                active={features.soulbound}
                onChange={() => toggleFeature("soulbound")}
              />
            </div>

            {features.whitelist && !features.publicMint && (
              <div className="mt-3 p-3 rounded-card bg-surface/50 border border-secondary/20 text-[11px] text-text-tertiary flex items-start gap-2 animate-fade-in">
                <Info className="w-3.5 h-3.5 text-secondary flex-shrink-0 mt-0.5" />
                <span>
                  Whitelist gating requires you to generate a Merkle root from your allowlist and set it
                  on the contract after deployment. Include the allowlist addresses in your collection config JSON.
                </span>
              </div>
            )}

            {features.soulbound && (
              <div className="mt-3 p-3 rounded-card bg-surface/50 border border-secondary/20 text-[11px] text-text-tertiary flex items-start gap-2 animate-fade-in">
                <Info className="w-3.5 h-3.5 text-secondary flex-shrink-0 mt-0.5" />
                <span>
                  Soulbound tokens override ERC-721 transfer functions with revert. They can still be burned
                  by the token owner in standard implementations.
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ── Right: Preview + JSON ───────────────────────────────────────── */}
        <div className="lg:col-span-2 space-y-4">
          <div className="sticky top-20 space-y-4">
            <h3 className="font-display text-sm font-semibold text-text-secondary uppercase tracking-wider">
              Live Preview
            </h3>

            <CollectionPreviewCard config={config} />

            <DeployBanner config={config} isConnected={isConnected} />

            <JSONPreview config={config} />

            {/* ERC-721 Standards note */}
            <div className="glass-card p-4">
              <h4 className="text-xs font-semibold text-text-secondary mb-3 uppercase tracking-wider flex items-center gap-1.5">
                <Image className="w-3.5 h-3.5" /> Standards Compliance
              </h4>
              <div className="space-y-2 text-[11px] text-text-tertiary leading-relaxed">
                <div className="flex items-start gap-1.5">
                  <Check className="w-3 h-3 text-secondary flex-shrink-0 mt-0.5" />
                  <span>ERC-721 — Non-fungible token standard</span>
                </div>
                <div className="flex items-start gap-1.5">
                  <Check className="w-3 h-3 text-secondary flex-shrink-0 mt-0.5" />
                  <span>ERC-721Metadata — Name, symbol, tokenURI</span>
                </div>
                <div className="flex items-start gap-1.5">
                  <Check className="w-3 h-3 text-secondary flex-shrink-0 mt-0.5" />
                  <span>EIP-2981 — On-chain royalty info</span>
                </div>
                <div className="flex items-start gap-1.5">
                  <Check className="w-3 h-3 text-secondary flex-shrink-0 mt-0.5" />
                  <span>OpenZeppelin Ownable — Admin control</span>
                </div>
                <div className="flex items-start gap-1.5">
                  <Check className="w-3 h-3 text-secondary flex-shrink-0 mt-0.5" />
                  <span>JasmyChain 680 compatible</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
